import React, { useEffect, useState } from 'react';
import CustomDrawer from 'components/shared/CustomDrawer/CustomDrawer';
import { Box, Typography, Divider } from '@mui/material';
import zeplinColor from 'theme/Color';
import AddLeadForm from './AddLeadForm';
import { LeadsService } from 'service/leads.service';
import { clearEditLeadData, getAssigneeDropdown } from 'store/slices/leadsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';

type AddLeadDrawerProps = {
  drawerState: boolean;
  setDrawerState: (visible: boolean) => void;
  onLeadAdded?: () => void;
  leadType?: string;
  leadTypeId?: string;
};

const AddLeadDrawer = ({
  drawerState,
  setDrawerState,
  onLeadAdded,
  leadType,
  leadTypeId,
}: AddLeadDrawerProps) => {
  const dispatch = useAppDispatch();
  const { isEditMode, editLead } = useAppSelector((state) => state.leads);
  const [typeDropdowns, setTypeDropdowns] = useState<any>(null);
  const [dropdownsLoading, setDropdownsLoading] = useState(false);
  const [dropdownError, setDropdownError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTypeDropdowns = async () => {
      if (!drawerState || !leadTypeId) {
        setTypeDropdowns(null);
        setDropdownError(null);
        return;
      }

      setDropdownsLoading(true);
      setDropdownError(null);

      try {
        const response = await LeadsService.getLeadTypeDropdowns(leadType || '');
        setTypeDropdowns(response);
        // dispatch(getAssigneeDropdown());
      } catch (error) {
        setDropdownError('Failed to load lead type options');
      } finally {
        setDropdownsLoading(false);
      }
    };

    fetchTypeDropdowns();
  }, [drawerState, leadTypeId]);

  const onClose = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerState(false);
    setTypeDropdowns(null);
    setDropdownError(null);
    dispatch(clearEditLeadData());
  };

  return (
    <CustomDrawer state={drawerState} onClose={onClose(false)}>
      <Box sx={{ width: { xs: '427px', xl: '527px' }, position: 'relative', overflowY: 'auto' }}>
        <Box
          sx={{
            zIndex: 1000,
            backgroundColor: zeplinColor.Neutral1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: { xs: '24px 24px 24px 24px', xl: '40px 24px 24px 24px' },
            }}
          >
            <Typography variant='responsiveHeading2'>
              {isEditMode ? `Edit Lead: ${editLead?.leadTypeName}` : `New Lead: ${leadType}`}
            </Typography>
            <img
              src='/assets/close-icon.svg'
              style={{ width: '24px', height: '24px', cursor: 'pointer' }}
              alt='close'
              onClick={onClose(false)}
            />
          </Box>
        </Box>
        <Divider sx={{ borderColor: zeplinColor.Neutral5 }} />
        <AddLeadForm
          onClose={onClose(false)}
          onLeadAdded={onLeadAdded}
          setDrawerState={setDrawerState}
          leadType={leadType}
          leadTypeId={leadTypeId}
          typeDropdowns={typeDropdowns}
          dropdownsLoading={dropdownsLoading}
          dropdownError={dropdownError}
        />
      </Box>
    </CustomDrawer>
  );
};

export default AddLeadDrawer;
